import React, { useState, useEffect } from 'react';
import DataService from '../services/dataService';
import { GalleryItem } from '../types';
import { useLanguage } from '../contexts/LanguageContext';
import { X } from 'lucide-react';

const Gallery: React.FC = () => {
  const [items, setItems] = useState<GalleryItem[]>(DataService.getGallery());
  const [selectedItem, setSelectedItem] = useState<GalleryItem | null>(null);
  const { language, t } = useLanguage();

  useEffect(() => {
    const handler = (e: any) => {
      const db = e?.detail?.db || DataService.getDB();
      if (db?.gallery) setItems(db.gallery);
    };
    window.addEventListener('db_updated', handler as EventListener);
    const db = DataService.getDB();
    if (db?.gallery) setItems(db.gallery);
    return () => window.removeEventListener('db_updated', handler as EventListener);
  }, []);

  const isVideo = (item: GalleryItem) => item.mediaType === 'video' || /\.(mp4|webm|ogg)$/i.test(item.url);

  return (
    <div className="py-12 px-4 bg-gray-50 min-h-screen">
      <div className="container mx-auto">
        <h1 className="text-4xl font-serif font-bold text-temple-orange-900 mb-4 text-center">
          {t('Photo Gallery')}
        </h1>
        <div className="columns-1 md:columns-2 lg:columns-3 gap-6 space-y-6">
          {items.map(item => (
            <div key={item.id} className="break-inside-avoid relative group cursor-pointer rounded-xl overflow-hidden shadow-md bg-white" onClick={() => setSelectedItem(item)}>
              {isVideo(item) ? (
                <div className="relative">
                  <video src={item.url} className="w-full h-auto object-cover transition-transform group-hover:scale-105" muted playsInline loop />
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="bg-white/90 rounded-full p-2 text-temple-orange-700">▶</div>
                  </div>
                </div>
              ) : (
                <img src={item.url} alt={item.caption} className="w-full h-auto object-cover transition-transform group-hover:scale-105" onError={e => (e.currentTarget.src = 'https://via.placeholder.com/800x600?text=Image')} />
              )}
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex items-end p-4">
                <p className="text-white font-medium">{language === 'ml' ? (item.captionMal || item.caption) : item.caption}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {selectedItem && (
        <div className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4" onClick={() => setSelectedItem(null)}>
          <div className="max-w-5xl max-h-[90vh] w-full relative p-4 bg-transparent" onClick={e => e.stopPropagation()}>
            <button className="absolute top-2 right-2 text-white" onClick={() => setSelectedItem(null)}><X size={22} /></button>
            {isVideo(selectedItem) ? (
              <video src={selectedItem.url} controls autoPlay className="max-w-full max-h-[80vh] object-contain rounded" />
            ) : (
              <img src={selectedItem.url} alt={selectedItem.caption} className="max-w-full max-h-[80vh] object-contain rounded" />
            )}
            <p className="text-white mt-4 text-center">{language === 'ml' ? (selectedItem.captionMal || selectedItem.caption) : selectedItem.caption}</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default Gallery;
